import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../lib/supabase';

type Salon = {
  id: string;
  name: string | null;
  state_code: string | null;
  license_number: string | null;
  created_at: string;
  stylist_count?: number;
  apprentice_count?: number;
};

export function Studios() {
  const [rows, setRows] = useState<Salon[] | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      const { data, error } = await supabase
        .from('salons')
        .select('id,name,state_code,license_number,created_at')
        .order('created_at', { ascending: false });
      if (error) {
        setErr(error.message);
        setRows([]);
        return;
      }
      const salons = (data ?? []) as Salon[];

      // Enrich with stylist + apprentice counts. Done client-side for MVP;
      // Phase 2 should move to a `backbar_salons_list()` SECURITY DEFINER RPC.
      const enriched = await Promise.all(salons.map(async (s) => {
        const [staffRes, appRes] = await Promise.all([
          supabase.from('stylists').select('id', { count: 'exact', head: true })
            .eq('salon_id', s.id).neq('role', 'apprentice'),
          supabase.from('stylists').select('id', { count: 'exact', head: true })
            .eq('salon_id', s.id).eq('role', 'apprentice'),
        ]);
        return { ...s, stylist_count: staffRes.count ?? 0, apprentice_count: appRes.count ?? 0 };
      }));
      setRows(enriched);
    })();
  }, []);

  return (
    <>
      <div className="page-head">
        <div>
          <div className="page-eyebrow">Tenants</div>
          <h1 className="page-title">Studios</h1>
          <p className="page-sub">Every salon on Rootegra. Drill in for staff and apprentice progress.</p>
        </div>
      </div>

      {err && (
        <div className="panel" style={{ marginBottom: 16, background: 'var(--warn-wash)' }}>
          <strong>Can't read salons:</strong> {err}.<br />
          Apply migration <code>037_admin_users.sql</code> so admin policies bypass RLS for backbar callers.
        </div>
      )}

      <section className="panel" style={{ padding: 0 }}>
        <div className="tbl-wrap">
        <table className="tbl">
          <thead>
            <tr>
              <th>Name</th>
              <th>State</th>
              <th>License</th>
              <th className="num">Stylists</th>
              <th className="num">Apprentices</th>
              <th>Created</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {rows == null && (
              <tr><td colSpan={7} className="muted" style={{ padding: 24, textAlign: 'center' }}>Loading…</td></tr>
            )}
            {rows?.length === 0 && !err && (
              <tr><td colSpan={7} className="muted" style={{ padding: 24, textAlign: 'center' }}>No salons yet.</td></tr>
            )}
            {rows?.map((s) => (
              <tr key={s.id}>
                <td><Link to={`/studios/${s.id}`}><strong>{s.name ?? '(unnamed)'}</strong></Link></td>
                <td>{s.state_code ?? '—'}</td>
                <td><code>{s.license_number ?? '—'}</code></td>
                <td className="num">{s.stylist_count ?? '—'}</td>
                <td className="num">{s.apprentice_count ?? '—'}</td>
                <td className="muted">{new Date(s.created_at).toLocaleDateString()}</td>
                <td><Link className="btn btn--ghost" to={`/studios/${s.id}`}>open →</Link></td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      </section>
    </>
  );
}
